<template>
  <div>
    <div class="d-flex align-items-center bg-light border px-3 py-2" style="border-radius: 10px; border-color: #e2e8f0 !important">
      <font-awesome-icon :icon="['fas', 'lock-alt']" size="lg" class="mr-2 fs-17" />
      <span class="text-muted flex-grow-1" style="min-width: 0">
        Solicitud <strong class="text-dark">en revisión</strong>.
        {{ canApprove ? 'Solicitud pendiente de revisión y aprobación' : 'Pendiente de aprobación del plano' }}
      </span>
      <template v-if="canApprove">
        <b-button
          variant="outline-danger"
          class="font-weight-bold flex-shrink-0 ml-2"
          style="border-radius: 8px"
          @click="openModal('reject')"
        >
          Rechazar
        </b-button>
        <b-button
          variant="outline-success"
          class="font-weight-bold flex-shrink-0 ml-2"
          style="border-radius: 8px"
          @click="openModal('approve')"
        >
          Aprobar
        </b-button>
      </template>
    </div>

    <ConfirmModal
      v-if="decision"
      :required="true"
      :disabled="processing || showUploader"
      modalWidth="min(600px, calc(100vw - 2rem))"
      @close="closeModal"
      @onConfirm="submit"
    >
      <template #header>
        <h5 class="mb-0">{{ config.title }}</h5>
      </template>
      <template #body>
        <b-overlay :show="showUploader" blur="3px" opacity="0.6" variant="white" rounded="lg">
          <template #overlay>
            <div class="bg-white shadow-lg rounded-lg p-3 text-left" style="width: 520px; max-width: 100%;">
              <div class="d-flex justify-content-between align-items-center mb-2">
                <h6 class="font-weight-bold text-dark mb-0">Adjuntar plano en PDF</h6>
                <button type="button" class="close" aria-label="Cerrar" @click="showUploader = false">&times;</button>
              </div>
              <AttachmentsUploader
                :allowedFileTypes="ALLOWED_FILE_TYPES"
                :maxNumberOfFiles="1"
                :maxFileSize="10485760"
                :useDashboard="true"
                :dashboardHeight="200"
                @file-added="onFileAdded"
                @file-removed="attachment = null"
                @handle-done-button="showUploader = false"
              />
            </div>
          </template>

          <div class="text-left">
            <label for="approval-reason" class="text-muted mb-1">
              Justificación<span class="text-danger ml-1">*</span>
            </label>
            <b-form-textarea
              id="approval-reason"
              v-model="reason"
              rows="4"
              no-resize
              :placeholder="config.placeholder"
              :state="reasonState"
              :disabled="processing"
            />
            <div class="d-flex justify-content-between fs-12 mt-1">
              <span class="text-muted">Mínimo {{ MIN_REASON_LENGTH }} caracteres</span>
            </div>

            <div class="mt-3">
              <div class="d-flex flex-wrap align-items-center justify-content-between" style="gap: 0.5rem;">
                <div>
                  <p class="mb-0 text-muted">Plano en PDF<span class="text-danger ml-1">*</span></p>
                  <small class="text-muted">1 archivo · máx. 10 MB</small>
                </div>
                <b-button
                  variant="outline-primary"
                  :disabled="!!attachment || processing"
                  @click="showUploader = true"
                >
                  <font-awesome-icon :icon="['far', 'paperclip']" fixed-width />
                  <span class="ml-1">Adjuntar archivo</span>
                  <small class="ml-1">{{ attachment ? 1 : 0 }}/1</small>
                </b-button>
              </div>

              <div v-if="attachment" class="d-flex flex-wrap bg-light rounded-lg p-2 mt-2" style="gap: 0.5rem;">
                <div
                  class="d-flex align-items-center border rounded-pill bg-white pl-3 pr-1"
                  style="max-width: 100%; height: 36px;"
                >
                  <font-awesome-icon :icon="['far', 'file-alt']" class="text-danger" fixed-width />
                  <span class="ml-1 text-dark text-truncate" style="font-size: 0.85rem;" :title="attachment.name">{{ attachment.name }}</span>
                  <button
                    type="button"
                    class="close ml-2 mr-1"
                    style="font-size: 1.2rem;"
                    :aria-label="`Quitar ${attachment.name}`"
                    :disabled="processing"
                    @click="attachment = null"
                  >
                    &times;
                  </button>
                </div>
              </div>
            </div>
          </div>
        </b-overlay>

        <p v-if="processing" class="text-info fs-14 mt-3 mb-0">
          <b-spinner small class="mr-1" />
          {{ config.processing }}
        </p>
        <b-alert v-if="errorMessage" show variant="danger" class="fs-14 mt-3 mb-0 py-2">
          {{ errorMessage }}
        </b-alert>
      </template>
    </ConfirmModal>
  </div>
</template>

<script>
import ConfirmModal from '../../shared/ConfirmModal'
import AttachmentsUploader from '../../shared/AttachmentsUploader.vue'

const MIN_REASON_LENGTH = 12
const ALLOWED_FILE_TYPES = ['.pdf']

const DECISIONS = {
  approve: {
    title: 'Aprobar plano',
    placeholder: 'Describa la razón de la aprobación…',
    processing: 'Aprobando...',
    toastTitle: 'Plano aprobado',
    toastMessage: 'El plano fue aprobado y la solicitud pasó a Entregado',
    toastVariant: 'success'
  },
  reject: {
    title: 'Rechazar plano',
    placeholder: 'Describa las correcciones que se deben realizar…',
    processing: 'Rechazando...',
    toastTitle: 'Plano rechazado',
    toastMessage: 'El plano fue rechazado y la solicitud pasó a Correcciones',
    toastVariant: 'warning'
  }
}

export default {
  name: 'DrawingRequestShowApprove',
  components: { ConfirmModal, AttachmentsUploader },
  props: {
    drawingRequestId: { type: Number, required: true },
    canApprove: { type: Boolean, default: false }
  },
  data () {
    return {
      MIN_REASON_LENGTH,
      ALLOWED_FILE_TYPES,
      decision: null,
      reason: '',
      attachment: null,
      showUploader: false,
      processing: false,
      errorMessage: null
    }
  },
  computed: {
    config () {
      return DECISIONS[this.decision] || {}
    },
    reasonLength () {
      return this.reason.trim().length
    },
    reasonValid () {
      return this.reasonLength >= MIN_REASON_LENGTH
    },
    reasonState () {
      if (this.reasonLength === 0) return null
      return this.reasonValid ? true : null
    },
    hasPdf () {
      return !!this.attachment &&
        this.attachment.type === 'document' &&
        /\.pdf$/i.test(this.attachment.name || '')
    }
  },
  methods: {
    openModal (decision) {
      this.reset()
      this.decision = decision
    },
    closeModal () {
      if (this.processing) return
      this.reset()
    },
    reset () {
      this.decision = null
      this.reason = ''
      this.attachment = null
      this.showUploader = false
      this.errorMessage = null
    },
    onFileAdded (form) {
      this.attachment = form
      this.showUploader = false
    },
    validationError () {
      if (!this.reasonValid) return `La justificación debe tener al menos ${MIN_REASON_LENGTH} caracteres`
      if (!this.hasPdf) return 'Debe adjuntar el plano en PDF'
      return null
    },
    submit () {
      if (this.processing) return

      const error = this.validationError()
      if (error) {
        this.errorMessage = error
        return
      }

      this.processing = true
      this.errorMessage = null
      const config = this.config

      this.$http.post(`/api/production/drawing_requests/${this.drawingRequestId}/approval`, {
        approval: { decision: this.decision, reason: this.reason.trim() },
        attachment: { file: this.attachment.file, name: this.attachment.name }
      })
        .then((res) => {
          this.processing = false
          this.closeModal()
          this.$emit('decided', res.data)
          this.$bvToast.toast(config.toastMessage, {
            title: config.toastTitle,
            variant: config.toastVariant,
            solid: true
          })
        })
        .catch((error) => {
          const response = error.response || {}
          const data = response.data || {}
          this.errorMessage = data.error || data.message || 'No se pudo completar la aprobación'
          this.processing = false

          if ([403, 409].includes(response.status)) this.$emit('stale')
        })
    }
  }
}
</script>

<style scoped>
#approval-reason ::placeholder {
  color: #5f6873;
  font-size: 16px;
}
</style>
